import type { Invoice } from '@monorepo/shared/schemas/invoices'
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100)
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatLabel(value: string): string {
  return value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    padding: 40,
    color: '#1a1a1a',
  },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
  companyName: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
  },
  invoiceTitle: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
    color: '#333',
  },
  invoiceNumber: {
    fontSize: 11,
    textAlign: 'right',
    color: '#666',
    marginTop: 4,
  },
  statusBadge: {
    fontSize: 9,
    textAlign: 'right',
    color: '#666',
    marginTop: 2,
    textTransform: 'uppercase',
  },

  // Sections
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#666',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Bill-to / Dates row
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  infoBlock: {
    width: '48%',
  },
  infoLabel: {
    fontSize: 9,
    color: '#888',
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 10,
    marginBottom: 4,
  },

  // Table
  table: {
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  colNum: { width: '6%' },
  colDesc: { width: '38%' },
  colQty: { width: '12%', textAlign: 'right' },
  colRate: { width: '16%', textAlign: 'right' },
  colAmount: { width: '18%', textAlign: 'right' },
  colDiscount: { width: '10%', textAlign: 'right' },
  headerText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: '#555',
  },
  cellText: {
    fontSize: 9,
  },

  // Totals
  totalsContainer: {
    alignItems: 'flex-end',
    marginBottom: 20,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: 250,
    paddingVertical: 3,
  },
  totalsLabel: {
    width: 130,
    textAlign: 'right',
    paddingRight: 12,
    fontSize: 10,
  },
  totalsValue: {
    width: 120,
    textAlign: 'right',
    fontSize: 10,
  },
  totalsBorder: {
    borderTopWidth: 1,
    borderTopColor: '#333',
    paddingTop: 4,
    marginTop: 2,
  },
  grandTotalLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
  },
  grandTotalValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
  },

  // Taxes breakdown
  taxesSection: {
    marginBottom: 16,
  },
  taxLine: {
    fontSize: 8,
    color: '#888',
    marginBottom: 2,
  },

  // Notes
  notesSection: {
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#fafafa',
    borderRadius: 4,
  },
  notesText: {
    fontSize: 9,
    color: '#555',
    lineHeight: 1.4,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: '#aaa',
  },
})

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface InvoicePdfDocumentProps {
  invoice: Invoice
  tenantName?: string
}

export function InvoicePdfDocument({
  invoice,
  tenantName,
}: InvoicePdfDocumentProps) {
  const currency = invoice.currency || 'USD'
  const lineItems = invoice.line_items || []
  const taxes = invoice.taxes || []

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        {/* ---- Header ---- */}
        <View style={styles.header}>
          <View>
            <Text style={styles.companyName}>
              {tenantName || 'Your Company'}
            </Text>
          </View>
          <View>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <Text style={styles.invoiceNumber}>{invoice.invoice_number}</Text>
            <Text style={styles.statusBadge}>
              {formatLabel(invoice.status)}
            </Text>
          </View>
        </View>

        {/* ---- Bill To & Dates ---- */}
        <View style={styles.infoRow}>
          {/* Bill To */}
          <View style={styles.infoBlock}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <Text style={styles.infoValue}>
              {invoice.customer?.name || 'Unknown Customer'}
            </Text>
            {invoice.customer?.email && (
              <Text style={[styles.infoValue, { color: '#666', fontSize: 9 }]}>
                {invoice.customer.email}
              </Text>
            )}
          </View>

          {/* Dates */}
          <View style={styles.infoBlock}>
            <Text style={styles.sectionTitle}>Invoice Details</Text>
            <View style={{ flexDirection: 'row', marginBottom: 3 }}>
              <Text style={[styles.infoLabel, { width: 90 }]}>Issue Date:</Text>
              <Text style={styles.infoValue}>
                {formatDate(invoice.issue_date)}
              </Text>
            </View>
            {invoice.due_date && (
              <View style={{ flexDirection: 'row', marginBottom: 3 }}>
                <Text style={[styles.infoLabel, { width: 90 }]}>Due Date:</Text>
                <Text style={styles.infoValue}>
                  {formatDate(invoice.due_date)}
                </Text>
              </View>
            )}
            {invoice.billing_period_from && invoice.billing_period_to && (
              <View style={{ flexDirection: 'row', marginBottom: 3 }}>
                <Text style={[styles.infoLabel, { width: 90 }]}>
                  Billing Period:
                </Text>
                <Text style={styles.infoValue}>
                  {formatDate(invoice.billing_period_from)} {' - '}
                  {formatDate(invoice.billing_period_to)}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* ---- Line Items Table ---- */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.headerText, styles.colNum]}>#</Text>
            <Text style={[styles.headerText, styles.colDesc]}>Description</Text>
            <Text style={[styles.headerText, styles.colQty]}>Qty</Text>
            <Text style={[styles.headerText, styles.colRate]}>Rate</Text>
            <Text style={[styles.headerText, styles.colDiscount]}>Disc.</Text>
            <Text style={[styles.headerText, styles.colAmount]}>Amount</Text>
          </View>

          {/* Table Rows */}
          {lineItems.map((item, index) => (
            <View style={styles.tableRow} key={item.id || `line-${index}`}>
              <Text style={[styles.cellText, styles.colNum]}>{index + 1}</Text>
              <Text style={[styles.cellText, styles.colDesc]}>
                {item.description}
              </Text>
              <Text style={[styles.cellText, styles.colQty]}>
                {item.quantity}
              </Text>
              <Text style={[styles.cellText, styles.colRate]}>
                {formatCurrency(item.unit_price_cents, currency)}
              </Text>
              <Text style={[styles.cellText, styles.colDiscount]}>
                {item.discount_cents
                  ? `-${formatCurrency(item.discount_cents, currency)}`
                  : '-'}
              </Text>
              <Text style={[styles.cellText, styles.colAmount]}>
                {formatCurrency(item.total_cents, currency)}
              </Text>
            </View>
          ))}
        </View>

        {/* ---- Totals ---- */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Subtotal</Text>
            <Text style={styles.totalsValue}>
              {formatCurrency(invoice.subtotal_cents, currency)}
            </Text>
          </View>

          {invoice.discount_cents > 0 && (
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Discount</Text>
              <Text style={[styles.totalsValue, { color: '#16a34a' }]}>
                -{formatCurrency(invoice.discount_cents, currency)}
              </Text>
            </View>
          )}

          {invoice.tax_cents > 0 && (
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>
                Tax
                {invoice.tax_rate
                  ? ` (${(invoice.tax_rate * 100).toFixed(1)}%)`
                  : ''}
              </Text>
              <Text style={styles.totalsValue}>
                {formatCurrency(invoice.tax_cents, currency)}
              </Text>
            </View>
          )}

          <View style={[styles.totalsRow, styles.totalsBorder]}>
            <Text style={[styles.totalsLabel, styles.grandTotalLabel]}>
              Grand Total
            </Text>
            <Text style={[styles.totalsValue, styles.grandTotalValue]}>
              {formatCurrency(invoice.total_cents, currency)}
            </Text>
          </View>
        </View>

        {/* ---- Taxes Breakdown ---- */}
        {taxes.length > 0 && (
          <View style={styles.taxesSection}>
            <Text style={styles.sectionTitle}>Tax Breakdown</Text>
            {taxes.map((tax, i) => (
              <Text style={styles.taxLine} key={tax.id || `tax-${i}`}>
                {tax.tax_name} ({(tax.tax_rate * 100).toFixed(1)}%) on{' '}
                {formatCurrency(tax.taxable_amount_cents, currency)} ={' '}
                {formatCurrency(tax.tax_amount_cents, currency)}
              </Text>
            ))}
          </View>
        )}

        {/* ---- Notes ---- */}
        {invoice.notes && (
          <View style={styles.notesSection}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text style={styles.notesText}>{invoice.notes}</Text>
          </View>
        )}

        {/* ---- Footer ---- */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{invoice.invoice_number}</Text>
          <Text style={styles.footerText}>
            Generated on{' '}
            {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Text>
        </View>
      </Page>
    </Document>
  )
}
